import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  Button,
  TablePagination,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Typography
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { tenantApiFetch } from './tenantApi';

const STATUSES = ['draft', 'issued', 'paid', 'overdue', 'void'];

function statusColor(s) {
  if (s === 'paid') return 'success';
  if (s === 'overdue' || s === 'void') return 'error';
  if (s === 'draft') return 'default';
  return 'info';
}

export default function TenantInvoicesListPage() {
  const [qInput, setQInput] = useState('');
  const [appliedQ, setAppliedQ] = useState('');
  const [statusInput, setStatusInput] = useState('');
  const [appliedStatus, setAppliedStatus] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const params = new URLSearchParams({ page: String(page + 1), pageSize: String(pageSize) });
    if (appliedQ) params.set('q', appliedQ);
    if (appliedStatus) params.set('invoiceStatus', appliedStatus);
    try {
      const res = await tenantApiFetch(`/api/tenant/billing/invoices?${params}`);
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Failed to load');
      setData(json);
    } catch (e) {
      setError(e.message);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, appliedQ, appliedStatus]);

  useEffect(() => {
    load();
  }, [load]);

  const apply = () => {
    setPage(0);
    setAppliedQ(qInput.trim());
    setAppliedStatus(statusInput);
  };

  const rows = data?.invoices || [];
  const total = data?.pagination?.total ?? 0;

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Read-only invoice history for your tenant.
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <TextField
            size="small"
            label="Search"
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && apply()}
          />
          <TextField
            size="small"
            label="Status"
            select
            value={statusInput}
            onChange={(e) => setStatusInput(e.target.value)}
            sx={{ minWidth: 140 }}
            SelectProps={{ native: true }}
          >
            <option value="">All</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </TextField>
          <Button variant="outlined" onClick={apply}>
            Apply
          </Button>
        </Box>
      </Paper>
      <Paper sx={{ overflow: 'auto' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Reference</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Period</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Issued</TableCell>
                  <TableCell>Due</TableCell>
                  <TableCell align="right">View</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{row.invoice_reference}</TableCell>
                    <TableCell>
                      {row.amount != null ? row.amount.toFixed(2) : '—'} {row.currency}
                    </TableCell>
                    <TableCell>{row.billing_period || '—'}</TableCell>
                    <TableCell>
                      <Chip size="small" label={row.invoice_status} color={statusColor(row.invoice_status)} variant="outlined" />
                    </TableCell>
                    <TableCell>{row.issued_at ? String(row.issued_at).slice(0, 10) : '—'}</TableCell>
                    <TableCell>{row.due_at ? String(row.due_at).slice(0, 10) : '—'}</TableCell>
                    <TableCell align="right">
                      <IconButton component={Link} to={`/tenant/billing/invoices/${row.id}`} size="small">
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {rows.length === 0 && (
              <Box sx={{ p: 3 }}>
                <Typography color="text.secondary">No invoices yet.</Typography>
              </Box>
            )}
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_e, p) => setPage(p)}
              rowsPerPage={pageSize}
              onRowsPerPageChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50]}
            />
          </>
        )}
      </Paper>
    </Box>
  );
}
